import { Component, AfterViewInit, OnDestroy, Input, OnChanges, SimpleChanges, NgZone, PLATFORM_ID, Inject, input, OnInit } from '@angular/core';
import { colorMapping } from './pie-chart.model';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { HttpClient } from '@angular/common/http';
import { DashboardService } from '../dashboard.service';
import { isPlatformBrowser } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { am5ChartLicense } from '../../../app.constants';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss'
})
export class PieChartComponent implements OnDestroy, OnChanges, OnInit {

  @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() categoryIds!: number[];
  @Input() companyId!: number;
  @Input() chartContainerId: string = 'barChartDiv' + Math.random().toString(36).substring(2, 9); // Unique ID for chart container

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes in inputs and update the chart
    if ( changes['categoryIds'] || changes['companyId'] || (changes['startDate']?.currentValue || changes['endDate']?.currentValue)) {
      this.loadChartData();
    }
  }

  ngOnInit(): void {
    const companyIdval = localStorage.getItem('companyId');
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
  }

  private root: am5.Root | undefined;

  constructor(private http: HttpClient, private dashboardService: DashboardService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object, private _snackBar: MatSnackBar
  ) { }

    loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId
    };
    if( !params.startDate || !params.endDate) {
      return;
    }
    if (this.root) {
      this.root.dispose();
      this.root = undefined; // Ensure root is undefined to prevent multiple initializations
    }
    this.dashboardService.getDashboardBookingsStorePercent(params).subscribe({
      next: (data: any[]) => {
      this.createChart(data);
      },
      error: (error) => 
        this._snackBar.open('Failed to load chart data. Please try again later.', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        })
    });
  }
  
  createChart(data: any[]) {
    this.browserOnly(() => {

      const element = document.getElementById(this.chartContainerId);
      if (!element) return;

      if (this.root) {
        this.root.dispose();
      }

      am5.addLicense(am5ChartLicense);

      const root = am5.Root.new(this.chartContainerId);
      const myTheme = am5.Theme.new(root);
      myTheme.rule('Grid', ['base']).setAll({
        strokeOpacity: 0.1
      });

      root.setThemes([
        am5themes_Animated.new(root),
        myTheme
      ]);
      root._logo?.dispose(); // Remove the logo to avoid clutter

      // Create chart
      const chart = root.container.children.push(
        am5percent.PieChart.new(root, {
          layout: root.verticalLayout
        })
      );

      // Create series
      const series = chart.series.push(
        am5percent.PieSeries.new(root, {
          name: 'Series',
          valueField: 'value',
          categoryField: 'category'
        })
      );

      // Map data fields
      const processedData = data.map(item => ({
        category: item.xValue || "Unknown",
        value: item.yValue || 0,
        color: am5.color(colorMapping[item.xValue] || '#000000')
      }));

      series.data.setAll(processedData);

      // Assign colors directly to each data item after the data is set
      series.slices.each((slice) => {
        const dataContext = slice.dataItem?.dataContext as { category: string };  // Access dataContext directly
        const category = dataContext.category;
        const color = colorMapping[category] || '#000000'; // Default to black if no mapping found
        slice.set("fill", am5.color(color)); // Set color for each slice
        // Set the border (stroke) properties
        slice.set("stroke", am5.color(color)); // Make the stroke (border) the same color as the slice
        slice.set("strokeWidth", 1); // Optional: Set to 0 to remove the border completely
      });

      // Add labels
      series.labels.template.setAll({
        text: '{category}: {value}',
        //fill: root.interfaceColors.get('alternativeText'),
        fill: am5.color("#000000") // This sets the text color to black
      });

      series.ticks.template.setAll({
        stroke: am5.color("#0000000"), // Sets the tick line color to black
        strokeWidth: 1, // Optionally, you can also control the thickness of the lines
        strokeOpacity: 0.3,
        visible: true
      });

      // Add legend
      const legend = chart.children.push(am5.Legend.new(root, {
        x: am5.p50,
        centerX: am5.p50
      }));

      legend.data.setAll(series.dataItems);

      // Animate chart
      series.appear(1000, 100);

      this.root = root;
    })
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose();
    }
  }
}
